import type { Currency } from '@sequencehq/api'
import {
  type AppliedGrantRow,
  toAppliedGrantCredit,
  toTotalAfterCreditGrants
} from '@sequencehq/invoice-content'
import { toMoney } from '@sequencehq/utils'

export const BALANCE_UNAVAILABLE = '—'

export type ListedInvoiceAmountBasis = 'gross' | 'afterAppliedGrants'

export type ListedInvoiceAmount = {
  currency: Currency
  grossTotal: string
  /** Applied grant credit including tax. */
  appliedCreditGrantTotal?: string
  appliedCreditGrants?: ReadonlyArray<AppliedGrantRow>
  appliedCreditGrantTax?: string | null
}

export type ListedInvoiceBalance = {
  currency: Currency
  balance?: string
}

export const toAmountBasis = (
  useCreditGrantLedger: boolean | undefined
): ListedInvoiceAmountBasis =>
  useCreditGrantLedger ? 'afterAppliedGrants' : 'gross'

/** The backend aggregate already includes tax; don't add per-grant tax again. */
const toCreditApplied = (invoice: ListedInvoiceAmount): string =>
  invoice.appliedCreditGrantTotal ??
  toAppliedGrantCredit({
    appliedCreditGrants: invoice.appliedCreditGrants ?? [],
    appliedCreditGrantTax: invoice.appliedCreditGrantTax
  })

/** Excludes projected credit: one unallocated balance may cover several drafts. */
export const toListedInvoiceAmountValue = ({
  invoice,
  basis
}: {
  invoice: ListedInvoiceAmount
  basis: ListedInvoiceAmountBasis
}): string =>
  toTotalAfterCreditGrants({
    grossTotal: invoice.grossTotal,
    creditApplied: basis === 'gross' ? '0' : toCreditApplied(invoice)
  })

export const toListedInvoiceAmount = (params: {
  invoice: ListedInvoiceAmount
  basis: ListedInvoiceAmountBasis
}): string =>
  toMoney({
    currency: params.invoice.currency,
    value: toListedInvoiceAmountValue(params)
  })

/** An unavailable balance must not look like a settled invoice. */
export const toListedInvoiceOutstanding = (
  invoice: ListedInvoiceBalance
): string =>
  invoice.balance === undefined
    ? BALANCE_UNAVAILABLE
    : toMoney({ currency: invoice.currency, value: invoice.balance })
